<template>
  <div>
    <!-- 一覧 -->
    <template v-if="!editingTemplateId">
      <div class="d-flex align-center">
        <div>
          <div class="text-subtitle-1">帳票レイアウト</div>
          <div class="text-caption text-grey-darken-1">
            同じ帳票種別のレイアウトを名前付きで複数持てます。帳票作成時にどれを使うか選びます。
          </div>
        </div>
        <v-spacer />
        <v-btn color="primary" variant="flat" @click="openCreateDialog(null)">
          <v-icon start>mdi-plus</v-icon>
          レイアウトを作成
        </v-btn>
      </div>

      <div v-if="loading" class="text-center py-8">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>

      <!--
        帳票種別ごとに表を分けるとヘッダーが4回繰り返されて列も揃わないため、
        1つの表にまとめて種別はグループ見出しの行にする。
      -->
      <v-table v-else density="compact" class="mt-3">
        <thead>
          <tr>
            <th>レイアウト名</th>
            <th style="width: 190px">公開状態</th>
            <th style="width: 170px">最終公開</th>
            <th style="width: 230px"></th>
          </tr>
        </thead>
        <tbody v-for="planType in planTypeOrder" :key="planType">
          <tr class="tp-template-group">
            <td colspan="4">{{ planTypeLabels[planType] }}</td>
          </tr>
          <tr v-for="template in templatesOf(planType)" :key="template.templateId">
            <td>{{ template.name }}</td>
            <td>
              <div class="d-flex align-center ga-1">
                <v-chip
                  :color="template.version === null ? 'grey' : 'success'"
                  size="small"
                  variant="flat"
                >
                  {{ template.version === null ? "未公開" : `第${template.version}版` }}
                </v-chip>
                <v-chip v-if="template.hasDraft" size="small" variant="outlined">
                  下書きあり
                </v-chip>
              </div>
            </td>
            <td class="text-caption">{{ formatDateTime(template.publishedAt) || "—" }}</td>
            <td class="text-right tp-template-actions">
              <v-btn size="small" variant="text" color="primary" @click="edit(template)">
                編集
              </v-btn>
              <v-btn size="small" variant="text" @click="openCreateDialog(template)">
                複製
              </v-btn>
              <v-btn
                size="small"
                variant="text"
                color="error"
                :loading="deletingTemplateId === template.templateId"
                @click="remove(template)"
              >
                削除
              </v-btn>
            </td>
          </tr>
          <tr v-if="templatesOf(planType).length === 0">
            <td colspan="4" class="text-center text-grey py-3">
              レイアウトがありません。
            </td>
          </tr>
        </tbody>
      </v-table>
    </template>

    <!-- 編集 -->
    <template v-else>
      <div class="d-flex align-center">
        <v-btn variant="text" size="small" @click="closeEditor">
          <v-icon start>mdi-chevron-left</v-icon>
          レイアウト一覧へ
        </v-btn>
      </div>
      <!--
        行が増えると保存ボタンが画面外へ出て、下の行を編集しながら保存できなくなる。
        ヘッダごと画面に追従させる (top はアプリバーの高さ40px + 余白)。
      -->
      <div class="d-flex align-center mt-1 tp-template-header">
        <div>
          <!--
            名称の変更は編集画面に置く。一覧に「編集」と「名前変更」が並ぶと
            同じ対象への操作が粒度違いで2つあり、押す前に選ばせることになる。
          -->
          <div v-if="!renaming" class="d-flex align-center ga-1">
            <div class="text-subtitle-1">{{ editingTemplate ? editingTemplate.name : "" }}</div>
            <v-btn
              size="x-small"
              variant="text"
              color="primary"
              :loading="savingName"
              @click="startRename"
            >
              <v-icon start size="14">mdi-pencil-outline</v-icon>
              名称を変更
            </v-btn>
          </div>
          <div v-else class="d-flex align-center ga-1">
            <v-text-field
              ref="nameInput"
              v-model="renameInput"
              label="レイアウト名"
              variant="outlined"
              density="compact"
              color="primary"
              hide-details
              autofocus
              style="min-width: 280px"
              :error="!isValidName(renameInput)"
              @keydown.enter="commitRename"
              @keydown.esc="cancelRename"
            ></v-text-field>
            <v-btn
              size="small"
              variant="flat"
              color="primary"
              :loading="savingName"
              :disabled="!isValidName(renameInput)"
              @click="commitRename"
            >
              名称を保存
            </v-btn>
            <v-btn size="small" variant="text" :disabled="savingName" @click="cancelRename">
              取消
            </v-btn>
          </div>
          <div class="text-caption text-grey-darken-1">
            <span v-if="editingTemplate">{{ planTypeLabels[editingTemplate.planType] }}</span>
            <span v-if="editingTemplate && editingTemplate.version !== null" class="ml-2">
              公開中: 第{{ editingTemplate.version }}版
            </span>
            <span v-else class="ml-2">未公開</span>
          </div>
        </div>
        <v-spacer />
        <!--
          未保存かどうかは isLayoutDirty で分かるのに画面に出しておらず、
          「下書きを保存」を押す必要があるのか判断できなかった。
        -->
        <span v-if="isLayoutDirty" class="text-caption text-warning mr-3 tp-template-dirty">
          <v-icon size="14">mdi-circle-medium</v-icon>
          未保存の変更があります
        </span>
        <v-btn
          class="mr-2"
          variant="outlined"
          color="primary"
          :loading="savingDraft"
          :disabled="layoutErrors.length > 0"
          @click="saveDraft"
        >
          下書きを保存
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="publishing"
          :disabled="layoutErrors.length > 0"
          @click="publish"
        >
          公開する
        </v-btn>
      </div>

      <v-alert v-if="layoutErrors.length > 0" type="warning" variant="tonal" density="compact" class="mt-3">
        <div v-for="error in layoutErrors.slice(0, 5)" :key="error">{{ error }}</div>
        <div v-if="layoutErrors.length > 5" class="text-caption">
          ほか{{ layoutErrors.length - 5 }}件
        </div>
      </v-alert>

      <v-alert
        v-if="editingTemplate && editingTemplate.version === null"
        type="info"
        variant="tonal"
        density="compact"
        class="mt-3"
      >
        公開するまで、このレイアウトで指導計画を作成することはできません。
      </v-alert>

      <div class="mt-3">
        <teaching-plan-layout-builder
          v-if="draftLayout"
          :key="editingTemplateId"
          v-model="draftLayout"
          @validity-change="layoutErrors = $event"
          @open-phrase="$emit('open-phrase', { templateId: editingTemplateId, phraseKey: $event })"
        ></teaching-plan-layout-builder>
      </div>

      <div class="mt-4">
        <div class="text-subtitle-2 mb-1">転記ルール</div>
        <div class="text-caption text-grey-darken-1 mb-2">
          このレイアウトに上位計画から値を引き込む設定です。
        </div>
        <!--
          上位帳票が無い種別 (全体的な計画) と未公開レイアウトは転記先にできない。
          押せてしまうと転記ルール側で選択肢に無いIDが入ってしまうため、理由を出して止める。
        -->
        <v-btn
          v-if="canEditTransferRule"
          variant="outlined"
          color="primary"
          size="small"
          @click="$emit('open-transfer-rule', editingTemplateId)"
        >
          <v-icon start>mdi-arrow-decision-outline</v-icon>
          このレイアウトの転記ルールを編集
        </v-btn>
        <div v-else class="text-caption text-grey-darken-1">
          {{ transferRuleDisabledReason }}
        </div>
      </div>
    </template>

    <!-- 作成・複製モーダル -->
    <v-dialog v-model="createDialog" max-width="560px" persistent>
      <v-card>
        <v-card-title>{{ copyFrom ? "レイアウトを複製" : "レイアウトを作成" }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="createInput.planType"
            :items="createPlanTypeItems"
            label="帳票種別"
            variant="outlined"
            density="compact"
            color="primary"
            class="mb-3"
            hide-details
            :disabled="copyFrom !== null"
          ></v-select>
          <v-text-field
            v-model="createInput.name"
            label="レイアウト名"
            variant="outlined"
            density="compact"
            color="primary"
            :counter="templateNameMaxLength"
            :hint="copyFrom ? `${copyFrom.name} の公開版を複製します` : '標準レイアウトから作成します'"
            persistent-hint
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="creating" @click="createDialog = false">
            キャンセル
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="creating"
            :disabled="!isValidName(createInput.name)"
            @click="create"
          >
            作成
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import {
  NurseryTeachingPlanLayout,
  NurseryTeachingPlanTemplateListItem,
  NurseryTeachingPlanTemplateResponse,
  NurseryTeachingPlanType,
} from "chaild-api/lib";
import ApiTeachingPlan from "@/api/ApiTeachingPlan";
import TeachingPlanLayoutBuilder from "@/components/teaching-plan/TeachingPlanLayoutBuilder.vue";
import TeachingPlanUtil from "@/utils/TeachingPlanUtil";
import {
  confirmAction,
  confirmDanger,
  confirmDiscard,
  notifySuccess,
} from "@/utils/TeachingPlanDialog";
import {
  adjacentUpperPlanType,
  createPlanTypeItems,
  planTypeLabels,
  planTypeOrder,
  templateNameMaxLength,
} from "@/constants/teachingPlan";

/**
 * 帳票レイアウトの一覧管理とビルダー。
 * 一覧APIは標準レイアウトの遅延シードも兼ねるため、初回表示で標準レイアウトが用意される。
 */
export default defineComponent({
  name: "TeachingPlanTemplateTab",

  components: { TeachingPlanLayoutBuilder },

  props: {
    nurseryId: {
      type: Number,
      required: true,
    },
  },

  emits: ["open-transfer-rule", "templates-updated", "open-phrase"],

  async mounted() {
    await this.load();
  },

  data() {
    return {
      loading: false,
      templates: [] as NurseryTeachingPlanTemplateListItem[],
      // 編集
      editingTemplateId: null as string | null,
      editingTemplate: null as NurseryTeachingPlanTemplateResponse | null,
      draftLayout: null as NurseryTeachingPlanLayout | null,
      /** 最後に保存・読み込みしたレイアウト (未保存判定用) */
      savedLayoutSnapshot: "",
      layoutErrors: [] as string[],
      savingDraft: false,
      publishing: false,
      // 作成・複製
      createDialog: false,
      creating: false,
      copyFrom: null as NurseryTeachingPlanTemplateListItem | null,
      createInput: {
        planType: "monthly" as NurseryTeachingPlanType,
        name: "",
      },
      // 名前変更
      /** 編集画面で名称を編集中か */
      renaming: false,
      savingName: false,
      renameInput: "",
      deletingTemplateId: null as string | null,
      // 定数
      planTypeOrder,
      planTypeLabels,
      createPlanTypeItems,
      templateNameMaxLength,
    };
  },

  computed: {
    /** 転記先になれるのは「上位帳票がある種別」かつ「公開済み」のレイアウトだけ */
    canEditTransferRule(): boolean {
      if (!this.editingTemplate) {
        return false;
      }
      return (
        adjacentUpperPlanType[this.editingTemplate.planType] !== null &&
        this.editingTemplate.version !== null
      );
    },
    transferRuleDisabledReason(): string {
      if (!this.editingTemplate) {
        return "";
      }
      if (adjacentUpperPlanType[this.editingTemplate.planType] === null) {
        return `${planTypeLabels[this.editingTemplate.planType]}は一番上の帳票のため、引き込む上位計画がありません。`;
      }
      if (this.editingTemplate.version === null) {
        return "このレイアウトを公開すると、転記ルールを設定できます。";
      }
      return "";
    },
    /** 編集中のレイアウトに未保存の変更があるか */
    isLayoutDirty(): boolean {
      if (!this.draftLayout) {
        return false;
      }
      return JSON.stringify(this.draftLayout) !== this.savedLayoutSnapshot;
    },
  },

  methods: {
    formatDateTime(value: string | null): string {
      return TeachingPlanUtil.formatDateTime(value);
    },
    isValidName(name: string): boolean {
      const trimmed = (name || "").trim();
      return trimmed.length > 0 && trimmed.length <= templateNameMaxLength;
    },
    templatesOf(planType: NurseryTeachingPlanType): NurseryTeachingPlanTemplateListItem[] {
      return this.templates.filter((template) => template.planType === planType);
    },
    async load() {
      this.loading = true;
      const response = await ApiTeachingPlan.listTemplates({
        nurseryId: this.nurseryId,
      }).catch(() => null);
      this.loading = false;
      if (response) {
        this.templates = response.data;
        this.$emit("templates-updated", response.data);
      }
    },

    /**
     * 編集
     */
    /** 転記ルールタブなど外部からレイアウト編集を開く */
    async editById(templateId: string) {
      if (this.templates.length === 0) {
        await this.load();
      }
      const target = this.templates.find((item) => item.templateId === templateId);
      if (target) {
        await this.edit(target);
      }
    },
    async edit(template: NurseryTeachingPlanTemplateListItem) {
      // 編集中に別レイアウトの編集へ直接移ると、未保存の変更が黙って消える
      // (一覧の「編集」だけでなく転記ルールタブからの openTemplateEditor 経由でも通る)
      if (
        this.isLayoutDirty &&
        !(await confirmDiscard("保存していないレイアウトの変更があります"))
      ) {
        return;
      }
      const response = await ApiTeachingPlan.findTemplate({
        templateId: template.templateId,
      }).catch(() => null);
      if (!response) {
        return;
      }
      this.editingTemplate = response;
      this.editingTemplateId = template.templateId;
      // 下書きが無ければ公開版を複製して編集を始める
      const base = response.draftLayout || response.layout;
      this.draftLayout = base
        ? (JSON.parse(JSON.stringify(base)) as NurseryTeachingPlanLayout)
        : { rows: [] };
      this.savedLayoutSnapshot = JSON.stringify(this.draftLayout);
    },
    async closeEditor() {
      if (
        this.isLayoutDirty &&
        !(await confirmDiscard("保存していないレイアウトの変更があります"))
      ) {
        return;
      }
      this.editingTemplateId = null;
      this.editingTemplate = null;
      this.draftLayout = null;
      this.savedLayoutSnapshot = "";
      this.layoutErrors = [];
      this.load();
    },
    async saveDraft() {
      if (!this.editingTemplateId || !this.draftLayout || this.layoutErrors.length > 0) {
        return;
      }
      this.savingDraft = true;
      // スナップショットは「実際に送った内容」で取る。応答後の draftLayout で取ると、
      // 保存中に行った編集まで保存済み扱いになり、離脱時に黙って失われる
      const sentSnapshot = JSON.stringify(this.draftLayout);
      const response = await ApiTeachingPlan.updateTemplate({
        templateId: this.editingTemplateId,
        draftLayout: this.draftLayout,
      }).catch(() => null);
      this.savingDraft = false;
      if (response) {
        this.editingTemplate = response;
        this.savedLayoutSnapshot = sentSnapshot;
        notifySuccess("下書きを保存しました");
      }
    },
    async publish() {
      if (!this.editingTemplateId || !this.draftLayout || this.layoutErrors.length > 0) {
        return;
      }
      if (
        !(await confirmAction(
          "このレイアウトを公開しますか？",
          "公開後に作成する指導計画から、このレイアウトが使われます。作成済みの指導計画は影響を受けません。",
          "公開する"
        ))
      ) {
        return;
      }
      this.publishing = true;
      // 未保存の変更を落とさないため公開前に下書きを保存する (スナップショットは送った内容で取る)
      const sentSnapshot = JSON.stringify(this.draftLayout);
      const saved = await ApiTeachingPlan.updateTemplate({
        templateId: this.editingTemplateId,
        draftLayout: this.draftLayout,
      }).catch(() => null);
      if (!saved) {
        this.publishing = false;
        return;
      }
      const response = await ApiTeachingPlan.publishTemplate({
        templateId: this.editingTemplateId,
      }).catch(() => null);
      this.publishing = false;
      if (response) {
        this.editingTemplate = response;
        this.savedLayoutSnapshot = sentSnapshot;
        // 公開状態が変わるので、他タブへ渡すレイアウト一覧も更新する
        await this.load();
        notifySuccess("レイアウトを公開しました");
      }
    },

    /**
     * 作成・複製・名前変更・削除
     */
    openCreateDialog(copyFrom: NurseryTeachingPlanTemplateListItem | null) {
      this.copyFrom = copyFrom;
      this.createInput = {
        planType: copyFrom ? copyFrom.planType : "monthly",
        name: copyFrom ? `${copyFrom.name}のコピー` : "",
      };
      this.createDialog = true;
    },
    async create() {
      if (!this.isValidName(this.createInput.name)) {
        return;
      }
      this.creating = true;
      const response = await ApiTeachingPlan.createTemplate({
        nurseryId: this.nurseryId,
        planType: this.createInput.planType,
        name: this.createInput.name.trim(),
        copyFromTemplateId: this.copyFrom ? this.copyFrom.templateId : undefined,
      }).catch(() => null);
      this.creating = false;
      if (response) {
        this.createDialog = false;
        await this.load();
        // 作成直後は未公開なので、そのまま編集画面へ入れる
        await this.edit({
          templateId: response.templateId,
          planType: response.planType,
          name: response.name,
          version: response.version,
          publishedAt: response.publishedAt,
          hasDraft: response.draftLayout !== null,
        });
      }
    },
    /**
     * このレイアウトの項目を転記元にしているルールの件数。
     * 転記ルールは転記先レイアウト単位で保存されるので、下位種別の
     * 公開済みレイアウト分だけ引いて項目キーで突き合わせる。
     */
    async countRulesUsingAsSource(
      template: NurseryTeachingPlanTemplateListItem
    ): Promise<number> {
      const lowerPlanType = (Object.keys(adjacentUpperPlanType) as NurseryTeachingPlanType[]).find(
        (planType) => adjacentUpperPlanType[planType] === template.planType
      );
      if (lowerPlanType == null || template.version === null) {
        return 0;
      }
      const detail = await ApiTeachingPlan.findTemplate({
        templateId: template.templateId,
      }).catch(() => null);
      const itemKeys = TeachingPlanUtil.itemKeyOrder(detail ? detail.layout : null);
      if (itemKeys.length === 0) {
        return 0;
      }
      const targets = this.templates.filter(
        (item) => item.planType === lowerPlanType && item.version !== null
      );
      const ruleLists = await Promise.all(
        targets.map((item) =>
          ApiTeachingPlan.listTransferRules({
            nurseryId: this.nurseryId,
            targetTemplateId: item.templateId,
          }).catch(() => null)
        )
      );
      return ruleLists
        .flatMap((list) => (list ? list.data : []))
        .filter((rule) => itemKeys.includes(rule.sourceItemKey)).length;
    },
    startRename() {
      if (!this.editingTemplate) {
        return;
      }
      this.renameInput = this.editingTemplate.name;
      this.renaming = true;
    },
    cancelRename() {
      this.renaming = false;
      this.renameInput = "";
    },
    async commitRename() {
      const templateId = this.editingTemplateId;
      if (templateId === null || !this.isValidName(this.renameInput)) {
        return;
      }
      const name = this.renameInput.trim();
      if (this.editingTemplate && name === this.editingTemplate.name) {
        this.cancelRename();
        return;
      }
      this.savingName = true;
      const response = await ApiTeachingPlan.updateTemplate({ templateId, name }).catch(
        () => null
      );
      this.savingName = false;
      // 応答が返る間に別のレイアウトへ移っていたら反映しない
      if (!response || this.editingTemplateId !== templateId) {
        return;
      }
      // editingTemplate は data なので、一覧を読み直しても見出しは変わらない。
      // 編集中の draftLayout を巻き込まないよう名前だけ差し替える
      if (this.editingTemplate) {
        this.editingTemplate.name = response.name ?? name;
      }
      this.cancelRename();
      await this.load();
    },
    async remove(template: NurseryTeachingPlanTemplateListItem) {
      // 転記元は項目キー指定でレイアウトに紐づかないため、このレイアウトを消しても
      // 下位レイアウトのルールは残り、実行時に「項目がありません」になる。
      // 消してから気づくと追いにくいので、件数を数えて先に知らせる
      const affected = await this.countRulesUsingAsSource(template);
      const sourceWarning =
        affected > 0
          ? ` このレイアウトの項目を転記元にしているルールが${affected}件あります。削除してもルールは残りますが、上位の帳票がこのレイアウトで作られていた場合は転記されなくなります。`
          : "";
      if (
        !(await confirmDanger(
          `「${template.name}」を削除しますか？`,
          `このレイアウトの転記ルールと文例も削除されます。${sourceWarning}`
        ))
      ) {
        return;
      }
      this.deletingTemplateId = template.templateId;
      const response = await ApiTeachingPlan.deleteTemplate({
        templateId: template.templateId,
      }).catch(() => null);
      this.deletingTemplateId = null;
      if (response) {
        await this.load();
      }
    },
  },
});
</script>

<style scoped>
/*
 * レイアウトは行数が増えるほど縦に伸びるので、保存操作を画面に残す。
 * top はアプリバー (v-app-bar height=40px) の下。
 */
.tp-template-header {
  position: sticky;
  top: 40px;
  z-index: 3;
  padding: 8px 0;
  background-color: #ffffff;
  border-bottom: 1px solid #eceff1;
}
.tp-template-dirty {
  white-space: nowrap;
}
/* 帳票種別の区切り。表は1つのままグループだけ分かるようにする */
/* 操作は4つ並ぶので折り返さない */
.tp-template-actions {
  white-space: nowrap;
}
.tp-template-group td {
  padding-top: 14px;
  font-size: 13px;
  font-weight: 700;
  color: #455a64;
  background-color: #f5f7f8;
}
</style>
